// lib/sand-v3/scene/SandSceneV3.tsx
'use client';

import { useEffect, useRef, useState } from 'react';
import { WebGLRenderer, Scene, OrthographicCamera, PlaneGeometry, Mesh, Vector2 } from 'three';
import { makeSandSurfaceMaterial } from '@/lib/sand-v3/shaders/sandSurface';
import { SAND_TIERS, detectSandTier, lowerSandTier } from '@/lib/sand-v3/quality/tiers';
import { makeFootprintTexture, syncFootprintTexture } from '@/lib/sand-v3/interaction/footprintField';
import { readStoneRects } from '@/lib/sand-v3/interaction/stoneRects';
import { SandStoneAnchors } from '@/lib/sand-v3/StoneAnchors';
import { StaticSandFallback } from '@/lib/sand/StaticSandFallback';
import { SandTexture } from '@/lib/sand/SandTexture';
import { createDepthMap, stampFootprint, imprintTap, decay, fadeRegion, resetWave } from '@/lib/sand/DepthMap';
import { makeFootprintImage, strideSide, type FootSide } from '@/lib/sand/Footprint';

const IDLE_RESET_MS = 90_000;
const RESET_DURATION_MS = 4_000;
const STRIDE_DISTANCE = 42;
const STRIDE_LATERAL_OFFSET = 8;
const STAMP_PIXEL_SCALE = 2;
const DEMO_KEY = 'fetti.sand.intro.v1';
const DEPTH_SCALE_BASE = 4; // viewport / footprintScale

/** Pure WebGL-availability probe (testable without a browser). */
export function canUseWebGL(): boolean {
  if (typeof document === 'undefined') return false;
  try {
    const c = document.createElement('canvas');
    return !!(c.getContext('webgl2') || c.getContext('webgl'));
  } catch {
    return false;
  }
}

export function SandSceneV3() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [fallback, setFallback] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const container = containerRef.current;
    if (!container) return;

    let tier = SAND_TIERS[detectSandTier()];
    if (tier.staticFallback || !canUseWebGL()) { setFallback(true); return; }

    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

    // ── Three setup ──────────────────────────────────────────────
    let renderer: WebGLRenderer;
    try {
      renderer = new WebGLRenderer({ antialias: false, alpha: false, powerPreference: 'high-performance' });
    } catch { setFallback(true); return; }
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, tier.dprCap));
    renderer.setSize(window.innerWidth, window.innerHeight);
    Object.assign(renderer.domElement.style, {
      position: 'fixed', inset: '0', zIndex: '0', pointerEvents: 'none',
    } as CSSStyleDeclaration);
    container.appendChild(renderer.domElement);

    const scene = new Scene();
    const camera = new OrthographicCamera(-1, 1, 1, -1, 0, 1);
    const material = makeSandSurfaceMaterial({ rings: tier.rings, specular: tier.specular, grain: tier.grain });
    const quad = new Mesh(new PlaneGeometry(2, 2), material);
    scene.add(quad);

    // ── Footprint depth field (existing CPU system) ──────────────
    let viewportW = window.innerWidth, viewportH = window.innerHeight;
    let map = createDepthMap(viewportW, viewportH, DEPTH_SCALE_BASE * (tier.footprintScale / 4));
    let footTex = makeFootprintTexture(map);
    material.uniforms.uFootprint.value = footTex;
    material.uniforms.uFootprintTexel.value = new Vector2(1 / map.width, 1 / map.height);

    const leftStamp = makeFootprintImage('left', STAMP_PIXEL_SCALE);
    const rightStamp = makeFootprintImage('right', STAMP_PIXEL_SCALE);
    const stampFor = (side: FootSide) => (side === 'left' ? leftStamp : rightStamp);

    // restore persisted sparse depth
    try {
      const saved = window.sessionStorage.getItem('sand_state');
      if (saved) {
        const p = JSON.parse(saved) as { width: number; height: number; sparse?: Array<[number, number]> };
        if (p.width === map.width && p.height === map.height && p.sparse) {
          for (const [i, v] of p.sparse) map.data[i] = v;
        }
      }
    } catch { /* ignore */ }

    function pushStones() {
      const { data, count } = readStoneRects(viewportW, viewportH);
      const arr = material.uniforms.uStones.value as { set: (x: number, y: number, z: number, w: number) => void }[];
      for (let i = 0; i < arr.length; i++) arr[i].set(data[i * 4], data[i * 4 + 1], data[i * 4 + 2], data[i * 4 + 3]);
      material.uniforms.uStoneCount.value = count;
    }

    function resize() {
      viewportW = window.innerWidth; viewportH = window.innerHeight;
      renderer.setSize(viewportW, viewportH);
      map = createDepthMap(viewportW, viewportH, DEPTH_SCALE_BASE * (tier.footprintScale / 4));
      footTex.dispose();
      footTex = makeFootprintTexture(map);
      material.uniforms.uFootprint.value = footTex;
      material.uniforms.uFootprintTexel.value = new Vector2(1 / map.width, 1 / map.height);
      material.uniforms.uResolution.value = new Vector2(viewportW, viewportH);
      pushStones();
    }
    material.uniforms.uResolution.value = new Vector2(viewportW, viewportH);
    pushStones();
    window.addEventListener('resize', resize);
    window.addEventListener('scroll', pushStones, { passive: true });

    // ── Interaction (ported verbatim from lib/sand/SandCanvas.tsx) ─
    let cursorX = -9999, cursorY = -9999, lastMoveAt = performance.now();
    let resetStartedAt = -1, strideAccum = 0, strideIndex = 0, cursorHeading = 0;
    let stoneRects: DOMRect[] = [];
    const readStones = () => { stoneRects = Array.from(document.querySelectorAll('[data-stone]')).map((el) => el.getBoundingClientRect()); };
    readStones();
    window.addEventListener('resize', readStones);
    const pointInStone = (x: number, y: number) =>
      stoneRects.some((r) => x >= r.left && x <= r.right && y >= r.top && y <= r.bottom);

    function placeFootprint(x: number, y: number, headingRad: number, side: FootSide) {
      const perpDir = side === 'left' ? -1 : 1;
      const px = x + Math.cos(headingRad + Math.PI / 2) * STRIDE_LATERAL_OFFSET * perpDir;
      const py = y + Math.sin(headingRad + Math.PI / 2) * STRIDE_LATERAL_OFFSET * perpDir;
      if (pointInStone(px, py)) return;
      const jitterRot = ((Math.random() - 0.5) * 8 * Math.PI) / 180;
      const jitterLat = (Math.random() - 0.5) * 4;
      const jx = px + Math.cos(headingRad + Math.PI / 2) * jitterLat;
      const jy = py + Math.sin(headingRad + Math.PI / 2) * jitterLat;
      stampFootprint(map, stampFor(side), jx, jy, headingRad + jitterRot);
    }

    function onPointerMove(e: PointerEvent) {
      const x = e.clientX, y = e.clientY;
      if (cursorX > -9000) {
        const dx = x - cursorX, dy = y - cursorY, stepLen = Math.hypot(dx, dy);
        if (stepLen > 0.5) {
          const stepHeading = Math.atan2(dy, dx);
          let dh = stepHeading - cursorHeading;
          while (dh > Math.PI) dh -= Math.PI * 2;
          while (dh < -Math.PI) dh += Math.PI * 2;
          cursorHeading += dh * 0.4;
          strideAccum += stepLen;
          while (strideAccum >= STRIDE_DISTANCE) {
            strideAccum -= STRIDE_DISTANCE; strideIndex += 1;
            placeFootprint(x, y, cursorHeading, strideSide(strideIndex));
          }
        }
      }
      cursorX = x; cursorY = y; lastMoveAt = performance.now();
    }
    window.addEventListener('pointermove', onPointerMove, { passive: true });

    function onPointerDown(e: PointerEvent) {
      if (e.pointerType !== 'touch') return;
      const side: FootSide = Math.random() < 0.5 ? 'left' : 'right';
      const x = e.clientX, y = e.clientY;
      if (!pointInStone(x, y)) stampFootprint(map, stampFor(side), x, y, Math.random() * Math.PI * 2);
      imprintTap(map, x, y);
    }
    window.addEventListener('pointerdown', onPointerDown, { passive: true });

    function onFocus(e: FocusEvent) {
      const t = e.target as HTMLElement | null; if (!t) return;
      const tag = t.tagName.toLowerCase();
      if (tag !== 'input' && tag !== 'textarea' && tag !== 'select') return;
      const rect = t.getBoundingClientRect();
      fadeRegion(map, rect.left + rect.width / 2, rect.top + rect.height / 2, 100, 0.5);
    }
    document.addEventListener('focusin', onFocus);

    const onResetEvent = () => { resetStartedAt = performance.now(); };
    window.addEventListener('sand:reset', onResetEvent);

    function onArrival(e: Event) {
      const detail = (e as CustomEvent<{ x: number; y: number }>).detail; if (!detail) return;
      const heading = -Math.PI / 2;
      const px = Math.cos(heading + Math.PI / 2), py = Math.sin(heading + Math.PI / 2);
      stampFootprint(map, leftStamp, detail.x + px * -STRIDE_LATERAL_OFFSET, detail.y + py * -STRIDE_LATERAL_OFFSET, heading, 1.2);
      stampFootprint(map, rightStamp, detail.x + px * STRIDE_LATERAL_OFFSET, detail.y + py * STRIDE_LATERAL_OFFSET, heading, 1.2);
      setTimeout(() => { resetStartedAt = performance.now(); }, 2400);
    }
    window.addEventListener('sand:arrival', onArrival as EventListener);

    // first-visit demo
    try {
      if (!window.sessionStorage.getItem(DEMO_KEY)) {
        window.sessionStorage.setItem(DEMO_KEY, '1');
        const heading = Math.PI / 4;
        const sx = viewportW * 0.12, sy = viewportH * 0.18, stX = (viewportW * 0.18) / 3, stY = (viewportH * 0.18) / 3;
        for (let i = 0; i < 3; i++) {
          const side: FootSide = i % 2 === 0 ? 'right' : 'left';
          setTimeout(() => placeFootprint(sx + stX * i, sy + stY * i, heading, side), 400 + i * 400);
        }
      }
    } catch { /* ignore */ }

    // ── render loop ──────────────────────────────────────────────
    let rafId = 0;
    let visible = !document.hidden;
    const onVis = () => { visible = !document.hidden; if (visible && !rafId && !reduced) rafId = requestAnimationFrame(tick); };
    document.addEventListener('visibilitychange', onVis);

    let warmFrames = 0, warmStart = 0;
    let tornDown = false;

    function tick(nowMs: number) {
      rafId = 0;
      if (!visible) return;
      decay(map);
      if (resetStartedAt < 0 && nowMs - lastMoveAt > IDLE_RESET_MS) resetStartedAt = nowMs;
      if (resetStartedAt > 0) {
        const p = (nowMs - resetStartedAt) / RESET_DURATION_MS;
        if (p >= 1) resetStartedAt = -1; else resetWave(map, p);
      }
      syncFootprintTexture(footTex, map);
      material.uniforms.uTime.value = nowMs / 1000;
      renderer.render(scene, camera);

      // warmup relief: if first ~30 frames overran 60fps, drop DPR one tier step
      if (warmFrames < 30) {
        if (warmFrames === 0) warmStart = nowMs;
        warmFrames++;
        if (warmFrames === 30) {
          const fps = 30_000 / (nowMs - warmStart);
          if (fps < 50) {
            tier = SAND_TIERS[lowerSandTier(tier.id)];
            if (tier.staticFallback) { cleanupToFallback(); return; }
            renderer.setPixelRatio(Math.min(window.devicePixelRatio, tier.dprCap));
          }
        }
      }
      if (!reduced) rafId = requestAnimationFrame(tick);
    }

    function cleanupToFallback() { teardown(); setFallback(true); }

    const onContextLost = (e: Event) => { e.preventDefault(); cleanupToFallback(); };
    renderer.domElement.addEventListener('webglcontextlost', onContextLost);

    (window as unknown as { __sandV3?: unknown }).__sandV3 = { renderer, scene, material, renderNow: () => renderer.render(scene, camera), get tier() { return tier.id; } };

    // one frame always; then loop unless reduced-motion
    rafId = requestAnimationFrame(tick);

    function teardown() {
      if (tornDown) return;
      tornDown = true;
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', resize);
      window.removeEventListener('resize', readStones);
      window.removeEventListener('scroll', pushStones);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('sand:reset', onResetEvent);
      window.removeEventListener('sand:arrival', onArrival as EventListener);
      document.removeEventListener('focusin', onFocus);
      document.removeEventListener('visibilitychange', onVis);
      renderer.domElement.removeEventListener('webglcontextlost', onContextLost);
      try {
        const sparse: Array<[number, number]> = [];
        for (let i = 0; i < map.data.length; i++) if (map.data[i] > 0.05) sparse.push([i, Math.round(map.data[i] * 100) / 100]);
        window.sessionStorage.setItem('sand_state', JSON.stringify({ width: map.width, height: map.height, sparse }));
      } catch { /* ignore */ }
      footTex.dispose();
      quad.geometry.dispose();
      material.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement);
      delete (window as unknown as { __sandV3?: unknown }).__sandV3;
    }

    return teardown;
  }, []);

  if (fallback) {
    return (<div ref={containerRef}><SandTexture /><StaticSandFallback force /></div>);
  }
  return (<div ref={containerRef}><SandStoneAnchors /></div>);
}
